import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "./api";

// Matches the cadence lib/warnings.js already established for "keep this
// fresh but don't hammer the API" screens: no new polling convention.
export const POLL_MS = 60000;

export const RANGE_PRESETS = [
  { id: "today", label: "Today", days: 1 },
  { id: "3d", label: "3 days", days: 3 },
  { id: "7d", label: "7 days", days: 7 },
  { id: "14d", label: "14 days", days: 14 },
  { id: "30d", label: "30 days", days: 30 },
];

export function rangeFromPreset(presetId, anchor = new Date()) {
  const preset = RANGE_PRESETS.find((p) => p.id === presetId) || RANGE_PRESETS[2];
  const start = new Date(anchor);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start.getTime() + preset.days * 24 * 3600 * 1000);
  return { start, end };
}

export async function fetchSchedule(params) {
  const { data } = await api.get("/fleet-calendar/schedule", { params });
  return data;
}

export async function fetchAvailabilitySummary(params) {
  const { data } = await api.get("/fleet-calendar/availability-summary", { params });
  return data;
}

export async function fetchSuggestedVehicles(params) {
  const { data } = await api.get("/fleet-calendar/suggest-vehicles", { params });
  return data;
}

function toParams({ start, end, category, availabilityState, bookingStatus, search }) {
  return {
    start: start.toISOString(),
    end: end.toISOString(),
    category: category?.length ? category.join(",") : undefined,
    availability_state: availabilityState?.length ? availabilityState.join(",") : undefined,
    booking_status: bookingStatus?.length ? bookingStatus.join(",") : undefined,
    search: search || undefined,
  };
}

/**
 * Fetches the schedule grid + availability summary together, refetches on
 * any filter/range change, and lightly polls (paused while a popover/drawer
 * is open) so Needs-Inspection clearing and other status changes show up
 * without a manual reload: no WebSocket/SSE, matching the app's existing
 * polling-only pattern (lib/warnings.js).
 */
export function useFleetSchedule({ start, end, category, availabilityState, bookingStatus, search, paused }) {
  const [schedule, setSchedule] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const timer = useRef(null);

  const load = useCallback(async () => {
    if (!start || !end) return;
    const params = toParams({ start, end, category, availabilityState, bookingStatus, search });
    try {
      const [sched, summ] = await Promise.all([fetchSchedule(params), fetchAvailabilitySummary(params)]);
      setSchedule(sched);
      setSummary(summ);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [start, end, category, availabilityState, bookingStatus, search]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (paused) return undefined;
    timer.current = setInterval(load, POLL_MS);
    return () => clearInterval(timer.current);
  }, [load, paused]);

  return { schedule, summary, loading, error, reload: load };
}
